import { useAppSelector, useAppDispatch } from 'store/hook';
import React, { useEffect } from 'react';
import { RootState } from 'store';
import Storage from 'utils/storage';
import { useTranslation } from 'react-i18next';
import { ConfigProvider, theme } from 'antd';
import { getUserDetail } from 'controller/auth';
import { SET_USER_INFO } from 'store/userReducer';
import './i18n/config';
import GoogleAuth from 'ui-component/googleAuth';
import enUS from 'antd/locale/en_US';
import zhCN from 'antd/locale/zh_CN';

// routing
import Routes from 'routes';

// project imports
import NavigationScroll from 'layout/NavigationScroll';
import { LANGUAGE } from 'constants/common';
import Websocket from 'ui-component/websocket';
import ValidatePassword from 'ui-component/verifyPasword';
import { initFingerprint } from 'utils/request';

const App = () => {
  const { i18n } = useTranslation();
  const loading = useAppSelector((state: RootState) => state.user.loading);
  const userId = useAppSelector((state: RootState) => state.user.user?.account?.userId);
  const payment = useAppSelector((state: RootState) => state.user.payment);

  const dispatch = useAppDispatch();

  useEffect(() => {
    const LANGUAGES = ['en', 'zh'];
    const navigatorLan = navigator.language.split('-')[0];
    const defaultLan = (LANGUAGES.includes(navigatorLan) && navigatorLan) || 'en';
    const currentLan = Storage.get(LANGUAGE, defaultLan, true);
    i18n.changeLanguage(currentLan);
  }, [i18n]);

  useEffect(() => {
    const getData = async () => {
      dispatch(
        SET_USER_INFO({
          loading: true
        })
      );
      await initFingerprint()

      // @ts-ignore
      if (window.location.pathname === '/begin' || window.location.pathname === '/login' || window.location.pathname === '/register') {
        dispatch(
          SET_USER_INFO({
            loading: false
          })
        );
        return;
      }
      Promise.all([getUserDetail()]).then(() => {
        dispatch(
          SET_USER_INFO({
            loading: false
          })
        );
      });
    };

    getData();
  }, [dispatch]);

  if (loading) return <div style={{ position: 'fixed', top: '10px', left: 0, right: 0, bottom: 0, backgroundColor: '#fff' }} />;

  return (
    <ConfigProvider
      locale={i18n.language === 'zh' ? zhCN : enUS}
      theme={{
        token: {
          colorPrimary: '#f63',
          colorTextSecondary: '#757B8C',
          colorTextTertiary: '#757B8C',
          colorSuccessBorder: '#BAE0FF',
          colorInfo: '#f63',
          borderRadius: 4
        },
        components: {
          Typography: {
            colorTextDescription: '#757B8C'
          },
          Breadcrumb: {
            itemColor: 'rgba(255,255,255, 0.45)',
            separatorColor: 'rgba(255,255,255, 0.45)',
            iconFontSize: 20,
            linkColor: 'rgba(255,255,255, 0.45)',
            fontSize: 20,
            fontSizeIcon: 16
          },
          Dropdown: {
            borderRadiusLG: 6
          },
          Menu: {
            dropdownWidth: 200,
            itemBorderRadius: 6,
            itemColor: '#ffffff',
            itemHoverColor: '#ffffff',
            horizontalItemHoverColor: '#f63',
            groupTitleColor: 'rgba(11,37,86, 0.45)',
            itemSelectedColor: '#f63',
            horizontalItemSelectedColor: '#f63',
            itemActiveBg: '#E5EEFF',
            itemSelectedBg: '#E5EEFF',
            iconSize: 16,
            groupTitleFontSize: 12
          },
          Tag: {
            borderRadiusSM: 4,
            fontSize: 12
          },
          Modal: {
            padding: 20
          },
          Tooltip: {
            colorBgSpotlight: '#7A7A7A',
            fontSize: 12
          }
        }
      }}
    >
      <NavigationScroll>
        <Routes />
        <Websocket />
        <GoogleAuth />
        {payment.openConfirmPayment && <ValidatePassword open={payment.openConfirmPayment} />}
      </NavigationScroll>
    </ConfigProvider>
  );
};

export default App;
